/**
 * Gantt Bar Generator Utility
 *
 * Pure functions for generating Gantt chart bar configurations based on
 * service availability schedules and booking slots.
 */
/**
 * Main orchestrator - generates all bars for a service on a given date
 */
export function generateAllBarsForService(service, schedule, slots, date, serviceDurationMinutes) {
    const bars = [];
    const dateStr = formatDateOnly(date);
    // Case 1: No schedule configured - entire day is unavailable
    if (!schedule) {
        bars.push(createUnavailableBar(`${dateStr} 00:00`, `${dateStr} 23:59`, 'No hay disponibilidad configurada'));
        bars.push(...slots.map((slot) => createBookedBar(slot, dateStr)));
        return bars;
    }
    // Case 2: Schedule exists but reservations disabled
    if (!schedule.allow_reservations) {
        const startTime = normalizeTimeFormat(schedule.start_time);
        const endTime = normalizeTimeFormat(schedule.end_time);
        bars.push(createUnavailableBar(`${dateStr} ${startTime}`, `${dateStr} ${endTime}`, 'Reservaciones deshabilitadas'));
        bars.push(...slots.map((slot) => createBookedBar(slot, dateStr)));
        return bars;
    }
    // Case 3: Schedule exists with reservations allowed
    // Normalize times from "HH:MM:SS" to "HH:MM" format
    const startTime = normalizeTimeFormat(schedule.start_time);
    const endTime = normalizeTimeFormat(schedule.end_time);
    const scheduleStartMins = timeToMinutes(startTime);
    const scheduleEndMins = timeToMinutes(endTime);
    // Add unavailable bar BEFORE schedule start (00:00 to start_time)
    if (scheduleStartMins > 0) {
        bars.push(createUnavailableBar(`${dateStr} 00:00`, `${dateStr} ${startTime}`, 'Fuera de horario'));
    }
    // Add unavailable bar AFTER schedule end (end_time to 23:59)
    const endOfDayMins = timeToMinutes('23:59');
    if (scheduleEndMins < endOfDayMins) {
        bars.push(createUnavailableBar(`${dateStr} ${endTime}`, `${dateStr} 23:59`, 'Fuera de horario'));
    }
    // Generate bars for the available window
    const durationMinutes = resolveDurationMinutes(serviceDurationMinutes, service.duration);
    bars.push(...generateAvailableWindowBars(slots, schedule, dateStr, durationMinutes));
    return bars;
}
export function resolveDurationMinutes(apiDurationMinutes, serviceDurationMinutes) {
    const apiDuration = Number(apiDurationMinutes);
    if (Number.isFinite(apiDuration) && apiDuration > 0) {
        return apiDuration;
    }
    const serviceDuration = Number(serviceDurationMinutes);
    if (Number.isFinite(serviceDuration) && serviceDuration > 0) {
        return serviceDuration;
    }
    return 60;
}
/**
 * Generate bars for the available time window (mix of available-empty and booked)
 */
function generateAvailableWindowBars(slots, schedule, dateStr, durationMinutes) {
    const bars = [];
    // Build time-indexed map of slots
    const slotMap = new Map();
    slots.forEach(slot => {
        const timeKey = slot.start_time.slice(0, 5); // Get HH:MM
        slotMap.set(timeKey, slot);
    });
    // Normalize schedule times from "HH:MM:SS" to "HH:MM"
    const startTime = normalizeTimeFormat(schedule.start_time);
    const endTime = normalizeTimeFormat(schedule.end_time);
    // Generate expected slots based on service duration
    const expectedSlots = generateExpectedSlotTimes(startTime, endTime, durationMinutes);
    // For each expected slot, create appropriate bar
    const renderedBookedSlotIds = new Set();
    expectedSlots.forEach(({ startTime, endTime }) => {
        const existingSlot = slotMap.get(startTime);
        if (existingSlot) {
            // Slot exists with potential bookings
            renderedBookedSlotIds.add(existingSlot.id);
            bars.push(createBookedBar(existingSlot, dateStr));
        }
        else {
            // Slot should exist but has no bookings - show as available-empty
            bars.push(createAvailableEmptyBar(`${dateStr} ${startTime}`, `${dateStr} ${endTime}`));
        }
    });
    slots
        .filter((slot) => !renderedBookedSlotIds.has(slot.id))
        .forEach((slot) => {
        bars.push(createBookedBar(slot, dateStr));
    });
    return bars;
}
/**
 * Create an unavailable bar (grayed out with diagonal stripes)
 */
export function createUnavailableBar(start, end, reason) {
    return {
        myBeginDate: start,
        myEndDate: end,
        ganttBarConfig: {
            id: `unavailable-${start}`,
            label: '',
            immobile: true,
            hasHandles: false,
            class: 'bar-unavailable',
            style: {
                background: 'repeating-linear-gradient(45deg, #f1f5f9, #f1f5f9 10px, #e2e8f0 10px, #e2e8f0 20px)',
                border: '1px solid #cbd5e1',
                opacity: '0.7',
                cursor: 'not-allowed',
            },
            metadata: { type: 'unavailable', reason },
        },
    };
}
/**
 * Create an available-empty bar (transparent with dashed border)
 */
export function createAvailableEmptyBar(start, end) {
    return {
        myBeginDate: start,
        myEndDate: end,
        ganttBarConfig: {
            id: `available-${start}`,
            label: '',
            immobile: true,
            hasHandles: false,
            class: 'bar-available-empty',
            style: {
                background: 'transparent',
                border: '2px dashed #22c55e',
                borderRadius: '0.5rem',
            },
            metadata: { type: 'available-empty' },
        },
    };
}
/**
 * Create a booked bar with utilization-based coloring
 */
export function createBookedBar(slot, dateStr) {
    const utilization = calculateUtilization(slot);
    return {
        myBeginDate: `${dateStr} ${slot.start_time.slice(0, 5)}`,
        myEndDate: `${dateStr} ${slot.end_time.slice(0, 5)}`,
        ganttBarConfig: {
            id: slot.id,
            label: formatSlotLabel(slot),
            utilization,
            immobile: true,
            slotData: slot,
            class: 'bar-booked',
            style: {
                background: getBarColor(utilization),
                borderRadius: '0.5rem',
            },
            metadata: { type: 'booked' },
        },
    };
}
/**
 * Calculate utilization percentage for a slot
 */
function calculateUtilization(slot) {
    const booked = slot.summary?.total_participants || 0;
    const total = slot.summary?.capacity?.total || 1;
    return Math.round((booked / total) * 100);
}
/**
 * Format the slot label to display booking count
 */
function formatSlotLabel(slot) {
    const bookingCount = slot.summary?.total_bookings || slot.bookings?.length || 0;
    const participants = slot.summary?.total_participants || 0;
    const capacity = slot.summary?.capacity?.total || 0;
    if (bookingCount === 0) {
        return '';
    }
    return `${bookingCount} reserva${bookingCount !== 1 ? 's' : ''} (${participants}/${capacity})`;
}
/**
 * Get bar color based on utilization percentage
 */
function getBarColor(utilization) {
    if (utilization >= 100)
        return '#ef4444'; // Red - full
    if (utilization >= 75)
        return '#f97316'; // Orange - high
    if (utilization > 0)
        return '#eab308'; // Yellow - partial
    return '#22c55e'; // Green - empty
}
/**
 * Generate expected slot times based on duration
 */
export function generateExpectedSlotTimes(startTime, endTime, durationMinutes) {
    const startMins = timeToMinutes(startTime);
    const endMins = timeToMinutes(endTime);
    const slots = [];
    for (let time = startMins; time + durationMinutes <= endMins; time += durationMinutes) {
        slots.push({
            startTime: minutesToTime(time),
            endTime: minutesToTime(time + durationMinutes),
        });
    }
    return slots;
}
/**
 * Normalize time format from "HH:MM:SS" to "HH:MM"
 * Handles both formats by taking only the first 5 characters
 */
export function normalizeTimeFormat(timeStr) {
    return timeStr.slice(0, 5);
}
/**
 * Convert time string (HH:MM) to minutes since midnight
 */
export function timeToMinutes(timeStr) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
}
/**
 * Convert minutes since midnight to time string (HH:MM)
 */
export function minutesToTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}
/**
 * Format date to YYYY-MM-DD string
 */
function formatDateOnly(date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}
